'use client';

import { useTranslations } from 'next-intl';
import { FileText, Plus, X } from 'lucide-react';
import type { StoredDocument } from '@/lib/tools-storage';

/**
 * Список недавних документов.
 *
 * Человек, закрывший вкладку, должен вернуться к своей работе, а не к
 * пустому полю. Документы лежат в его браузере (IndexedDB), поэтому список
 * не требует ни входа, ни сервера.
 */
export function RecentDocuments({
  items,
  activeId,
  onOpen,
  onCreate,
  onRemove,
}: {
  items: StoredDocument[];
  activeId: string;
  onOpen: (doc: StoredDocument) => void;
  onCreate: () => void;
  onRemove: (id: string) => void;
}) {
  const t = useTranslations('dh_editor');

  return (
    <aside className="lg:w-64 shrink-0">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold">{t('recent')}</h2>
        <button
          onClick={onCreate}
          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border border-[var(--color-border)] hover:border-[var(--color-accent-500)] transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          {t('new_document')}
        </button>
      </div>

      {items.length === 0 ? (
        <p className="text-xs text-[var(--color-text-muted)] leading-relaxed">
          {t('recent_empty')}
        </p>
      ) : (
        <ul className="space-y-1 max-h-[320px] lg:max-h-[540px] overflow-y-auto pr-1">
          {items.map((doc) => (
            <li key={doc.id} className="group flex items-stretch gap-1">
              <button
                onClick={() => {
                  onOpen(doc);
                }}
                aria-current={doc.id === activeId ? 'true' : undefined}
                className={`flex-1 flex items-start gap-2 text-left px-2.5 py-2 rounded-lg text-sm transition-colors ${
                  doc.id === activeId
                    ? 'bg-[var(--color-bg-elevated)] text-[var(--color-text)]'
                    : 'text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)]/60'
                }`}
              >
                <FileText className="w-4 h-4 shrink-0 mt-0.5 opacity-60" />
                <span className="truncate">{doc.title}</span>
              </button>

              <button
                onClick={() => {
                  onRemove(doc.id);
                }}
                aria-label={t('delete')}
                title={t('delete')}
                className="px-1.5 rounded-lg text-[var(--color-text-muted)] opacity-0 group-hover:opacity-100 focus-visible:opacity-100 hover:text-[var(--color-text)] transition-opacity"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-4 text-xs text-[var(--color-text-muted)]/80 leading-relaxed">
        {t('local_note')}
      </p>
    </aside>
  );
}
